import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext";

interface RequireAuthProps {
  children: ReactNode;
  /** If set, the route requires the current admin's role to be in this
   * list — used for super_admin-only pages like Admin Users and the Audit
   * Log. This is a UI convenience only, hiding a nav item / redirecting
   * away from a page a moderator can't use — the REAL enforcement is
   * server-side (requireRole in the Worker); a moderator who somehow
   * reaches the page anyway still gets a 403 from every API call it makes.
   */
  roles?: Array<"super_admin" | "moderator">;
}

export function RequireAuth({ children, roles }: RequireAuthProps) {
  const { session, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(session.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
